import { useConfirmation } from '@/app/providers';
import { Badge, Button, DataTable, Select, type ColumnDef } from '@/core/ui/components';
import type { EmailTemplate } from '@yawi/types';
import { Copy, Mail, MoreVertical, Pencil, Plus, Star, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEmailTemplates } from '../hooks/useEmailTemplates';

// El backend no tiene columna `type`: la plantilla se clasifica por `code`
// (magic-link, welcome, password-reset, ...), así que el filtro va por ahí.
const CODE_LABELS: Record<string, string> = {
  'magic-link': 'Enlace mágico',
  welcome: 'Bienvenida',
  'password-reset': 'Restablecer Contraseña',
  'payment-reminder': 'Recordatorio de Pago',
  'ticket-update': 'Actualización de Ticket',
  'reservation-confirmed': 'Confirmación de Reserva',
  'invitation-created': 'Invitación',
  generic: 'Personalizado',
};

const CODE_OPTIONS = [
  { value: '', label: 'Todos los códigos' },
  { value: 'magic-link', label: 'Enlace mágico' },
  { value: 'welcome', label: 'Bienvenida' },
  { value: 'password-reset', label: 'Restablecer Contraseña' },
  { value: 'payment-reminder', label: 'Recordatorio de Pago' },
  { value: 'ticket-update', label: 'Actualización de Ticket' },
  { value: 'reservation-confirmed', label: 'Confirmación de Reserva' },
  { value: 'invitation-created', label: 'Invitación' },
  { value: 'generic', label: 'Personalizado' },
];

export function EmailTemplateListPage() {
  const navigate = useNavigate();
  const { confirm } = useConfirmation();
  const {
    templates,
    pagination,
    isLoading,
    selectedType,
    setSelectedType,
    search,
    setSearch,
    page,
    setPage,
    pageSize,
    setPageSize,
    deleteTemplate,
    duplicateTemplate,
    setDefaultTemplate,
  } = useEmailTemplates();

  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  const handleViewTemplate = (template: EmailTemplate) => {
    navigate(`/email-templates/${template.id}`);
  };

  const handleDuplicate = async (template: EmailTemplate) => {
    try {
      const newTemplate = await duplicateTemplate(template.id);
      navigate(`/email-templates/${newTemplate.id}`);
    } catch {
      // Error already handled in hook
    }
    setOpenMenuId(null);
  };

  const handleSetDefault = async (template: EmailTemplate) => {
    try {
      await setDefaultTemplate(template.id);
    } catch {
      // Error already handled in hook
    }
    setOpenMenuId(null);
  };

  const handleDelete = async (template: EmailTemplate) => {
    const confirmed = await confirm({
      title: 'Eliminar Plantilla',
      message: `¿Estás seguro de que deseas eliminar "${template.name}"? Esta acción no se puede deshacer.`,
      variant: 'danger',
      confirmText: 'Eliminar',
    });

    if (confirmed) {
      try {
        await deleteTemplate(template.id);
      } catch {
        // Error already handled in hook
      }
    }
    setOpenMenuId(null);
  };

  const columns: ColumnDef<EmailTemplate>[] = [
    {
      id: 'name',
      header: 'Plantilla',
      cell: (template) => (
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
            <Mail className="w-5 h-5 text-purple-600 dark:text-purple-400" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <p className="font-medium text-gray-900 dark:text-white">{template.name}</p>
              {template.isDefault && (
                <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
              )}
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-xs">
              {template.subject}
            </p>
          </div>
        </div>
      ),
    },
    {
      id: 'code',
      header: 'Código',
      cell: (template) => (
        <Badge variant="purple">{CODE_LABELS[template.code] ?? template.code}</Badge>
      ),
    },
    {
      id: 'status',
      header: 'Estado',
      cell: (template) => (
        <Badge variant={template.isActive ? 'success' : 'default'}>
          {template.isActive ? 'Activo' : 'Inactivo'}
        </Badge>
      ),
    },
    {
      id: 'variables',
      header: 'Variables',
      cell: (template) => (
        <span className="text-gray-600 dark:text-gray-400">
          {template.variables?.length || 0}
        </span>
      ),
    },
    {
      id: 'updatedAt',
      header: 'Actualizado',
      cell: (template) => (
        <span className="text-gray-600 dark:text-gray-400">
          {new Date(template.updatedAt).toLocaleDateString()}
        </span>
      ),
    },
  ];

  const renderRowActions = (template: EmailTemplate) => (
    <div className="relative">
      <button
        onClick={(e) => {
          e.stopPropagation();
          setOpenMenuId(openMenuId === template.id ? null : template.id);
        }}
        className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
      >
        <MoreVertical className="w-4 h-4 text-gray-500 dark:text-gray-400" />
      </button>

      {openMenuId === template.id && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpenMenuId(null)} />
          <div className="absolute right-0 top-full mt-1 w-48 bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 shadow-lg z-20 py-1">
            <button
              onClick={() => {
                handleViewTemplate(template);
                setOpenMenuId(null);
              }}
              className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              <Pencil className="w-4 h-4" />
              Editar
            </button>
            <button
              onClick={() => handleDuplicate(template)}
              className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              <Copy className="w-4 h-4" />
              Duplicar
            </button>
            {!template.isDefault && (
              <button
                onClick={() => handleSetDefault(template)}
                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                <Star className="w-4 h-4" />
                Marcar como Predeterminada
              </button>
            )}
            <div className="border-t border-gray-200 dark:border-gray-700 my-1" />
            <button
              onClick={() => handleDelete(template)}
              className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              <Trash2 className="w-4 h-4" />
              Eliminar
            </button>
          </div>
        </>
      )}
    </div>
  );

  const filters = (
    <Select
      value={selectedType || ''}
      onChange={(e) => setSelectedType(e.target.value || null)}
      options={CODE_OPTIONS}
      className="w-48"
    />
  );

  return (
    <div className="h-full">
      <DataTable
        title="Plantillas de Correo"
        data={templates}
        columns={columns}
        keyExtractor={(template) => template.id}
        loading={isLoading}
        emptyMessage="No hay plantillas de correo"
        onRowClick={handleViewTemplate}
        rowActions={renderRowActions}
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Buscar plantillas..."
        filters={filters}
        meta={pagination}
        onPageChange={setPage}
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
        actions={
          <Button
            leftIcon={<Plus className="w-4 h-4" />}
            onClick={() => navigate('/email-templates/new')}
          >
            Nueva Plantilla
          </Button>
        }
        minHeight="min-h-[calc(100vh-180px)]"
      />
    </div>
  );
}
